/**
 * VoteOptionCard — renders a single voting option with appropriate UI
 * based on vote type (approve-veto, single-choice, ranked, thumbs).
 */
import React from 'react';
import type { VoteOption, Vote, VoteResult as VoteResultType, VotingCardProperties } from '@/types/pm-types';

interface VoteOptionCardProps {
  option: VoteOption;
  voteType: VotingCardProperties['voteType'];
  myVote?: Vote;
  result?: VoteResultType;
  showResults: VotingCardProperties['showResults'];
  showVoterNames: boolean;
  hasVoted: boolean;
  isResolved: boolean;
  accentColor?: string;
  disabled?: boolean;
  voters?: Vote[];
  onVote: (optionId: string, decision: 'approve' | 'veto' | 'abstain') => void;
  rank?: number;
}

export const VoteOptionCard: React.FC<VoteOptionCardProps> = ({
  option,
  voteType,
  myVote,
  result,
  showResults,
  showVoterNames,
  hasVoted,
  isResolved,
  accentColor = '#22C55E',
  disabled = false,
  voters = [],
  onVote,
  rank,
}) => {
  const isApproved = myVote?.decision === 'approve';
  const isVetoed = myVote?.decision === 'veto';
  const resolvedApproved = isResolved && result && result.approveCount > result.vetoCount;
  const resolvedVetoed = isResolved && result && result.vetoCount > 0 && result.vetoCount >= result.approveCount;

  const shouldShowResults =
    showResults === 'always' ||
    (showResults === 'after-vote' && hasVoted) ||
    isResolved;

  const borderColor = isApproved
    ? accentColor
    : isVetoed
      ? '#EF4444'
      : '#E5E7EB';

  const bgColor = resolvedApproved
    ? '#F0FDF4'
    : resolvedVetoed
      ? '#FEF2F2'
      : 'white';

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || isResolved) return;
    if (voteType === 'single-choice' || voteType === 'ranked') {
      onVote(option.id, 'approve');
    } else {
      // approve-veto / thumbs: clicking card body toggles approve
      onVote(option.id, isApproved ? 'abstain' : 'approve');
    }
  };

  return (
    <div
      onClick={handleCardClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 8,
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${borderColor}`,
        background: bgColor,
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {/* Option header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Rank badge for ranked type */}
        {voteType === 'ranked' && rank !== undefined && (
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            background: '#F3F4F6', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#374151',
            flexShrink: 0,
          }}>
            {rank}
          </span>
        )}

        {/* Single-choice radio indicator */}
        {voteType === 'single-choice' && (
          <span style={{
            width: 16, height: 16, borderRadius: '50%',
            border: `2px solid ${isApproved ? accentColor : '#D1D5DB'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {isApproved && (
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: accentColor,
              }} />
            )}
          </span>
        )}

        {/* Status icon */}
        {isApproved && voteType !== 'single-choice' && (
          <span style={{ color: accentColor, fontSize: 14, flexShrink: 0 }}>&#x2713;</span>
        )}
        {isVetoed && (
          <span style={{ color: '#EF4444', fontSize: 14, flexShrink: 0 }}>&#x2717;</span>
        )}

        {/* Option image */}
        {option.image && (
          <img
            src={option.image}
            alt={option.label}
            style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
          />
        )}

        {/* Label & description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#1F2937', lineHeight: 1.3 }}>
            {option.label}
          </div>
          {option.description && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, lineHeight: 1.3 }}>
              {option.description}
            </div>
          )}
        </div>

        {/* Resolved badge */}
        {isResolved && result && (
          <span style={{
            fontSize: 10, fontWeight: 500, padding: '2px 6px', borderRadius: 3,
            background: resolvedApproved ? '#DCFCE7' : resolvedVetoed ? '#FEE2E2' : '#F3F4F6',
            color: resolvedApproved ? '#166534' : resolvedVetoed ? '#991B1B' : '#6B7280',
            flexShrink: 0,
          }}>
            {resolvedApproved ? 'Approved' : resolvedVetoed ? 'Vetoed' : 'Pending'}
          </span>
        )}
      </div>

      {/* Vote buttons */}
      {!isResolved && !disabled && (
        <div style={{ display: 'flex', gap: 6 }}>
          {(voteType === 'approve-veto' || voteType === 'thumbs') && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onVote(option.id, isApproved ? 'abstain' : 'approve'); }}
                style={{
                  flex: 1, padding: '5px 0', borderRadius: 5, fontSize: 11, fontWeight: 500,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s ease',
                  background: isApproved ? accentColor : '#F3F4F6',
                  color: isApproved ? 'white' : '#374151',
                }}
              >
                {voteType === 'thumbs' ? '\u{1F44D}' : ''} {isApproved ? 'Approved' : 'Approve'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onVote(option.id, isVetoed ? 'abstain' : 'veto'); }}
                style={{
                  flex: 1, padding: '5px 0', borderRadius: 5, fontSize: 11, fontWeight: 500,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s ease',
                  background: isVetoed ? '#EF4444' : '#F3F4F6',
                  color: isVetoed ? 'white' : '#374151',
                }}
              >
                {voteType === 'thumbs' ? '\u{1F44E}' : ''} {isVetoed ? 'Vetoed' : 'Veto'}
              </button>
            </>
          )}
          {voteType === 'single-choice' && (
            <button
              onClick={(e) => { e.stopPropagation(); onVote(option.id, 'approve'); }}
              style={{
                flex: 1, padding: '5px 0', borderRadius: 5, fontSize: 11, fontWeight: 500,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s ease',
                background: isApproved ? accentColor : '#F3F4F6',
                color: isApproved ? 'white' : '#374151',
              }}
            >
              {isApproved ? 'Selected' : 'Select'}
            </button>
          )}
        </div>
      )}

      {/* Results bar (inline) */}
      {shouldShowResults && result && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            flex: 1, height: 4, borderRadius: 2, overflow: 'hidden',
            background: '#E5E7EB', display: 'flex',
          }}>
            {result.approveCount > 0 && (
              <div style={{
                width: `${(result.approveCount / Math.max(result.approveCount + result.vetoCount + result.abstainCount, 1)) * 100}%`,
                background: accentColor,
                transition: 'width 0.3s ease',
              }} />
            )}
            {result.vetoCount > 0 && (
              <div style={{
                width: `${(result.vetoCount / Math.max(result.approveCount + result.vetoCount + result.abstainCount, 1)) * 100}%`,
                background: '#EF4444',
                transition: 'width 0.3s ease',
              }} />
            )}
          </div>
          <span style={{ fontSize: 10, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
            {result.approveCount}&#x2191; {result.vetoCount}&#x2193;
          </span>
        </div>
      )}

      {/* Voter avatars */}
      {showVoterNames && voters.length > 0 && shouldShowResults && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {voters.map((v) => (
            <span
              key={v.voterId}
              title={v.voterName}
              style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#F3F4F6',
                border: `2px solid ${v.decision === 'approve' ? accentColor : v.decision === 'veto' ? '#EF4444' : '#D1D5DB'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 600, color: '#374151',
              }}
            >
              {v.voterName?.charAt(0)?.toUpperCase() || '?'}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

VoteOptionCard.displayName = 'VoteOptionCard';
